import { Wordmark } from "../components/Logo.tsx";
import { Link, useParams } from "react-router";
import { Text, makeStyles, tokens } from "@fluentui/react-components";

const OPERATOR = { name: "Copper Beech AI Ltd", number: "NI739924", address: "107 Old Kilmore Road, Moira, Craigavon, Northern Ireland, BT67 0NA", email: "privacy@copperbeechai.com", site: "https://copperbeechai.com" };
export const POLICY_VERSION = "2026-11-04";

const useStyles = makeStyles({
  page: { maxWidth: "760px", margin: "0 auto", padding: "32px 24px", lineHeight: "1.55" },
  nav: { display: "flex", gap: "16px", marginBottom: "24px", fontSize: tokens.fontSizeBase200 },
  h2: { marginTop: "28px" },
});

/** /privacy, /privacy/trainers, /terms — plain-language legal pages. Not legal advice; review before launch. */
export function LegalPage() {
  const s = useStyles();
  const { doc } = useParams();
  const which = doc === "trainers" ? "trainers" : doc === "terms" ? "terms" : "clients";
  return (
    <div className={s.page}>
      <Wordmark height={30} />
      <nav className={s.nav}>
        <Link to="/privacy">Privacy notice (clients)</Link>
        <Link to="/privacy/trainers">Privacy notice (trainers)</Link>
        <Link to="/privacy/terms">Terms & data processing (trainers)</Link>
      </nav>
      {which === "clients" && <ClientNotice s={s} />}
      {which === "trainers" && <TrainerNotice s={s} />}
      {which === "terms" && <Terms s={s} />}
      <Text size={200} block style={{ marginTop: 32, color: tokens.colorNeutralForeground3 }}>
        Version {POLICY_VERSION}. {OPERATOR.name}, company no. {OPERATOR.number}, {OPERATOR.address}. Questions or requests: {OPERATOR.email}.
      </Text>
    </div>
  );
}

type S = ReturnType<typeof useStyles>;

function ClientNotice({ s }: { s: S }) {
  return (
    <>
      <h1>Privacy notice for clients</h1>
      <p>Your personal trainer uses RackUp to organise your training sessions. This notice explains what happens to your information.</p>
      <h2 className={s.h2}>Who is responsible</h2>
      <p>
        <b>Your trainer</b> is the "controller" of your information — they decide why it is collected. <b>{OPERATOR.name}</b> ("RackUp", "we") runs the RackUp service on their behalf as a "processor". If you have a question about your data, you can ask your trainer or email us at {OPERATOR.email}.
      </p>
      <h2 className={s.h2}>What we hold</h2>
      <ul>
        <li>Your name, WhatsApp phone number and (optionally) email address. Your trainer may give us your name and number first, so RackUp can send you a sign-up link on their behalf; until you've signed up, that's all we hold and all we use it for.</li>
        <li>Your date of birth, and your gender if you choose to tell us (you can pick "prefer not to say", or describe it in your own words).</li>
        <li>How many sessions a week you train, your preferred days, times and gyms.</li>
        <li>Your booked sessions.</li>
        <li>WhatsApp messages between you and RackUp (for example your weekly availability replies). We keep message text for 90 days.</li>
        <li>A record of when you agreed to this notice and to receiving WhatsApp messages.</li>
        <li>Usage events (for example "check-in sent", "replied after 3 hours"), linked to an ID rather than your name and never including what you wrote — so we can tell whether RackUp is working. Kept 12 months; deleted with your data.</li>
      </ul>
      <h2 className={s.h2}>Why, and on what legal basis</h2>
      <ul>
        <li><b>To schedule your sessions</b> — this is necessary for the service you and your trainer have agreed (contract).</li>
        <li><b>Your date of birth</b> — to check you're 18 or over, and so your trainer knows your age when planning your training, including training plans they set up for you in RackUp. This is part of the service you've agreed with your trainer (contract).</li>
        <li><b>To refer to you correctly</b> — if you tell us your gender, RackUp uses it only to choose the right pronouns (she, he or they) when it mentions you to your trainer. It doesn't change anything else. It's optional (legitimate interests).</li>
        <li>Your trainer can see your age and gender. Neither is sent to the AI model, and you can change or remove both on your personal page at any time.</li>
        <li><b>To message you on WhatsApp</b> about availability and bookings — with your consent, which you can withdraw at any time by replying <b>STOP</b>.</li>
        <li><b>To understand your WhatsApp replies</b>, we use an AI model (Microsoft Azure OpenAI, hosted in the EU) that reads your message and your preferences. It sees your first name only, never your phone number or email. Nothing you send is used to train AI models.</li>
      </ul>
      <h2 className={s.h2}>Who else sees it</h2>
      <ul>
        <li><b>Microsoft Azure</b> (hosting and AI; UK and EU data centres).</li>
        <li><b>Meta / WhatsApp</b> (to deliver messages; WhatsApp's own privacy policy applies to your account).</li>
        <li>Nobody else. We never sell or share your information for advertising.</li>
      </ul>
      <h2 className={s.h2}>How long we keep it</h2>
      <p>Message text: 90 days. Weekly check-in records: 12 months. Session history: 2 years. If you have no sessions or check-ins for 12 months, your details are deleted automatically. You can delete everything sooner — see below.</p>
      <h2 className={s.h2}>Your rights</h2>
      <p>You can, at any time using your personal RackUp link (ask your trainer for it, or reply to any RackUp message):</p>
      <ul>
        <li><b>See and download</b> everything we hold about you ("Download my data").</li>
        <li><b>Correct</b> your details and preferences.</li>
        <li><b>Stop WhatsApp messages</b> (reply STOP, or switch it off on your page).</li>
        <li><b>Delete your data</b> ("Delete my data"). Your trainer is told you've left; your name is removed from their records.</li>
      </ul>
      <p>You also have the right to complain to the Information Commissioner's Office (ico.org.uk) in the UK, or your local data protection authority in the EU.</p>
      <h2 className={s.h2}>Age</h2>
      <p>RackUp is for people aged 18 or over.</p>
    </>
  );
}

function TrainerNotice({ s }: { s: S }) {
  return (
    <>
      <h1>Privacy notice for trainers</h1>
      <p>This covers your own account information. For your clients' information, see the <Link to="/privacy/terms">data processing terms</Link>.</p>
      <h2 className={s.h2}>Controller</h2>
      <p>{OPERATOR.name} (company no. {OPERATOR.number}), {OPERATOR.address}. Contact: {OPERATOR.email}.</p>
      <h2 className={s.h2}>What we hold and why</h2>
      <ul>
        <li>Name, email, password (stored as a one-way hash), phone number — to run your account and send you scheduling notifications (contract).</li>
        <li>Your gyms, availability and settings — to build schedules (contract). When you look up your gym, what you type is sent to Microsoft Azure Maps to search; if you choose "Use my location", your browser asks first and your location is used for that one search only — we never store it, only the gym you pick.</li>
        <li>Sign-in records (device, IP address, time) — security (legitimate interests).</li>
        <li>Subscription and billing status; card details are held by Stripe, never by us (contract).</li>
        <li>An audit log of actions on client data (exports, deletions) — accountability under data protection law (legal obligation).</li>
        <li>Usage analytics — which features you use (event names and counts such as "setup completed"), linked to your account ID and never including message content, kept 12 months and deleted with your account — to improve RackUp (legitimate interests). We don't use third-party trackers.</li>
        <li>Anonymised, aggregated usage to improve defaults — for example, the most common session lengths and hours become the suggested answers during setup. Only counts are kept, an answer is shown only once many trainers have given it, and nothing you type is shown to another trainer.</li>
      </ul>
      <h2 className={s.h2}>Sub-processors</h2>
      <p>Microsoft Azure (hosting, database, AI — UK South / Sweden Central), Meta WhatsApp Business Platform (messaging), Stripe (payments, when enabled).</p>
      <h2 className={s.h2}>Retention</h2>
      <p>While your account is open, plus 30 days. Audit logs: 3 years. You can export or delete your account from Settings at any time; deleting your account erases your clients' data too.</p>
      <h2 className={s.h2}>Your rights</h2>
      <p>Access, rectification, erasure, restriction, portability and objection — via Settings or {OPERATOR.email}. Complaints: ico.org.uk.</p>
    </>
  );
}

function Terms({ s }: { s: S }) {
  return (
    <>
      <h1>Terms of service & data processing terms</h1>
      <p>Between you (the "Trainer") and {OPERATOR.name} ("RackUp"). By creating an account you agree to these terms.</p>
      <h2 className={s.h2}>1. The service</h2>
      <p>RackUp schedules training sessions between you and your clients, including automated WhatsApp messaging and AI-assisted interpretation of client replies. You decide the schedule; RackUp proposes it.</p>
      <h2 className={s.h2}>2. Your responsibilities as controller</h2>
      <ul>
        <li>You are the controller of your clients' personal data. RackUp is your processor.</li>
        <li>Only add a client when they have agreed to be added and to receive WhatsApp messages from RackUp on your behalf, and point them to the client privacy notice. Where you add a client yourself, RackUp sends them a notice and STOP instructions in the first message.</li>
        <li>Only add clients aged 18 or over.</li>
        <li>Handle client requests about their data (access, correction, deletion) using the tools in RackUp, within one month.</li>
      </ul>
      <h2 className={s.h2}>3. RackUp's obligations as processor (UK GDPR Art. 28 / EU GDPR Art. 28)</h2>
      <ul>
        <li>Process client data only on your documented instructions (which these terms and your use of the service constitute), and only for scheduling and messaging.</li>
        <li>Ensure staff with access are bound by confidentiality.</li>
        <li>Apply appropriate security: encryption in transit and at rest, access controls, audit logging, hashed passwords, signed session cookies.</li>
        <li>Use only the sub-processors listed in the trainer privacy notice; give you 30 days' notice by email before adding one, with the right to terminate.</li>
        <li>Assist you with data subject requests and with your security and impact-assessment obligations.</li>
        <li>Delete or return client data at the end of the service (account deletion erases it; export is available first).</li>
        <li>Make available the information needed to demonstrate compliance, and allow audits on reasonable notice.</li>
        <li>Notify you without undue delay, and in any case within 48 hours, after becoming aware of a personal data breach affecting your clients.</li>
        <li>Store and process data in the UK and EU only.</li>
      </ul>
      <h2 className={s.h2}>4. Fees</h2>
      <p>RackUp is free during the trial period. Paid plans will be announced in the app before any charge is made; you will need to actively subscribe.</p>
      <h2 className={s.h2}>5. Acceptable use</h2>
      <p>No unsolicited marketing, no use for anyone other than your own clients, no attempts to circumvent security or rate limits.</p>
      <h2 className={s.h2}>6. Liability</h2>
      <p>RackUp is provided with reasonable skill and care. We are not liable for missed sessions or losses arising from schedules you have approved or from WhatsApp delivery failures. Nothing limits liability that cannot be limited by law.</p>
      <h2 className={s.h2}>7. Termination</h2>
      <p>You can delete your account at any time from Settings. We may suspend accounts that breach these terms, with notice where practicable.</p>
      <h2 className={s.h2}>8. Law</h2>
      <p>Northern Ireland law; courts of Northern Ireland. UK GDPR and the Data Protection Act 2018 apply; where clients or trainers are in the EU, the EU GDPR applies to that processing.</p>
    </>
  );
}
